import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { WEEKDAY_COLORS } from './weekColors'

function hexToRgba (hex, alpha) {
  const m = hex.replace('#', '').match(/.{2}/g)
  if (!m) return `rgba(0,0,0,${alpha})`
  const [r, g, b] = m.map(c => parseInt(c, 16))
  return `rgba(${r},${g},${b},${alpha})`
}

const BLOB_RADIUS_PX = 40
const REDRAW_DEBOUNCE_MS = 100

export default function WeekDensityLayer ({ mapRef, stops, activeDay, hoverDay }) {
  const map = mapRef && typeof mapRef.addLayer === 'function' ? mapRef : mapRef?.current
  const canvasRef = useRef(null)
  const paneRef = useRef(null)

  useEffect(() => {
    if (!map) return
    const PANE_NAME = 'weekDensity'
    if (!map.getPane(PANE_NAME)) {
      map.createPane(PANE_NAME)
      map.getPane(PANE_NAME).style.zIndex = 410
      map.getPane(PANE_NAME).style.pointerEvents = 'none'
    }
    paneRef.current = map.getPane(PANE_NAME)

    const canvas = document.createElement('canvas')
    canvas.style.position = 'absolute'
    canvas.style.top = '0'
    canvas.style.left = '0'
    canvas.style.pointerEvents = 'none'
    paneRef.current.appendChild(canvas)
    canvasRef.current = canvas

    return () => {
      if (canvasRef.current?.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current)
      }
      canvasRef.current = null
    }
  }, [map])

  useEffect(() => {
    if (!map || !canvasRef.current) return

    const draw = () => {
      const canvas = canvasRef.current
      if (!canvas) return
      const size = map.getSize()
      canvas.width = size.x
      canvas.height = size.y

      const topLeft = map.containerPointToLayerPoint([0, 0])
      L.DomUtil.setPosition(canvas, topLeft)

      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, size.x, size.y)
      ctx.globalCompositeOperation = 'lighter'

      const visibleStops = stops.filter(s => {
        if (activeDay != null && s.dayIndex !== activeDay) return false
        if (activeDay == null && hoverDay != null && s.dayIndex !== hoverDay) return false
        return true
      })

      for (const stop of visibleStops) {
        const pt = map.latLngToContainerPoint([stop.lat, stop.lng])
        const color = WEEKDAY_COLORS[stop.dayIndex] || '#999'
        const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, BLOB_RADIUS_PX)
        grad.addColorStop(0, hexToRgba(color, 0.55))
        grad.addColorStop(1, hexToRgba(color, 0))
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(pt.x, pt.y, BLOB_RADIUS_PX, 0, 2 * Math.PI)
        ctx.fill()
      }
    }

    draw()

    let timer
    const onMoveEnd = () => {
      clearTimeout(timer)
      timer = setTimeout(draw, REDRAW_DEBOUNCE_MS)
    }
    map.on('moveend zoomend', onMoveEnd)
    return () => {
      clearTimeout(timer)
      map.off('moveend zoomend', onMoveEnd)
    }
  }, [map, stops, activeDay, hoverDay])

  return null
}
